import { createRoute, type AnyRoute } from "@tanstack/react-router";
import { LineChart } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import { PostHogPage } from "./pages/PostHogPage";

export type PostHogTab = "overview" | "goals" | "settings" | "how-to";

export interface PostHogSearch {
  tab?: PostHogTab;
  /** Rolling window in days. 7 = this week vs last week. */
  days?: number;
  /** Feature key whose goal drawer is open. */
  feature?: string;
  /** Drawer is in expanded (full-page) mode rather than quick view. */
  expanded?: boolean;
}

const TABS: readonly PostHogTab[] = ["overview", "goals", "settings", "how-to"];
const WINDOWS = [7, 14, 30, 90];

function validatePostHogSearch(search: Record<string, unknown>): PostHogSearch {
  const out: PostHogSearch = {};
  if (TABS.includes(search.tab as PostHogTab)) out.tab = search.tab as PostHogTab;
  const days = Number(search.days);
  if (WINDOWS.includes(days)) out.days = days;
  if (typeof search.feature === "string" && search.feature) out.feature = search.feature;
  if (search.expanded === true || search.expanded === "true") out.expanded = true;
  return out;
}

/**
 * Product-usage analytics. Sits under System rather than Tools because it's
 * about the product itself, not about the business the product reports on.
 *
 * `posthog.read` is granted directly, never via a "System" category grant —
 * this page shows per-person activity for every member of staff. See
 * CATEGORY_GRANT_EXEMPT in backend/app/core/permissions/categories.py.
 */
export const posthogModule: FeatureModule = {
  key: "posthog",
  nav: {
    label: "PostHog",
    icon: LineChart,
    to: "/system/posthog",
    requires: "posthog.read",
    order: 30,
    group: "System",
  },
  routes: (rootRoute) =>
    [
      createRoute({
        getParentRoute: () => rootRoute,
        path: "/system/posthog",
        component: guarded("posthog.read", PostHogPage),
        validateSearch: validatePostHogSearch,
      }),
    ] as unknown as AnyRoute[],
};
